import { Component, DestroyRef, HostListener, OnInit, ViewChild, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { MatSidenav } from '@angular/material/sidenav';
import {
  ActivatedRoute,
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  ResolveEnd,
  RouteConfigLoadEnd,
  RouteConfigLoadStart,
  Router,
} from '@angular/router';
import { filter } from 'rxjs/operators';
import { ConsentService } from './common/services/consent.service';
import { FeaturesService } from './common/services/features.service';
import { SeoMetadata, SeoService } from './common/services/seo.service';
import { ThemeService } from './common/services/theme.service';
import { FeedbackDialogComponent } from './common/shared/feedback-dialog.component';
import { AuthService } from './common/user_and_auth/auth.service';
import { UserService } from './common/user_and_auth/user.service';
import { CharacterCloudSyncService } from './character-profile/character-cloud-sync.service';

/** Width threshold below which the sidenav switches to overlay mode. */
const MOBILE_BREAKPOINT = 768;

/** Number of rapid clicks on the title to toggle labs mode. */
const LABS_CLICK_THRESHOLD = 5;
/** Max time between clicks (ms) to count as rapid. */
const LABS_CLICK_WINDOW = 3000;

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false
})
export class AppComponent implements OnInit {
  @ViewChild('sidenav') sidenav!: MatSidenav;

  readonly isMobile = signal(window.innerWidth < MOBILE_BREAKPOINT);
  readonly loadingRoute = signal(false);

  /** Delay before showing the bar so quick navigations don't flicker. */
  private loadingShowTimer: ReturnType<typeof setTimeout> | null = null;

  private destroyRef = inject(DestroyRef);
  private consent = inject(ConsentService);
  private route = inject(ActivatedRoute);
  readonly features = inject(FeaturesService);

  private labsClicks: number[] = [];

  /** Eagerly instantiated so its login effect fires and its hook is registered with the profile service. */
  private cloudSync = inject(CharacterCloudSyncService);

  constructor(public router: Router, private seo: SeoService, public user: UserService, public theme: ThemeService, private dialog: MatDialog, private auth: AuthService) {
  }

  ngOnInit() {
    this.theme.init();
    this.consent.initAnalytics();

    // Check for ?labs=1 / ?labs=0 query param
    this.route.queryParamMap.subscribe(params => {
      const labs = params.get('labs');
      if (labs === '1') this.features.setLabsMode(true);
      else if (labs === '0') this.features.setLabsMode(false);
    });

    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      if (this.sidenav && this.isMobile()) this.sidenav.close();
      this.seo.setMetadata(this.collectRouteSeoData(), { isDefault: true });
    });

    // Show a progress bar while a route is loading. Lazy-loaded modules trigger
    // RouteConfigLoadStart; NavigationStart covers eager routes too. Use a small
    // delay so fast navigations don't flash the bar.
    this.router.events.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(event => {
      if (event instanceof NavigationStart || event instanceof RouteConfigLoadStart) {
        this.scheduleLoadingShow();
      } else if (
        event instanceof NavigationEnd ||
        event instanceof NavigationCancel ||
        event instanceof NavigationError ||
        event instanceof RouteConfigLoadEnd ||
        event instanceof ResolveEnd
      ) {
        this.cancelLoadingShow();
      }
    });
  }

  /**
   * Walk the activated route tree to the deepest matched child and return its
   * `data.seo`, if any. Components with dynamic content (build name, class
   * name, parsed log) can override by calling SeoService.setMetadata
   * themselves after their data resolves.
   */
  private collectRouteSeoData(): SeoMetadata {
    let route = this.route.snapshot;
    while (route.firstChild) route = route.firstChild;
    return (route.data?.['seo'] as SeoMetadata) ?? {};
  }

  private scheduleLoadingShow(): void {
    if (this.loadingShowTimer || this.loadingRoute()) return;
    this.loadingShowTimer = setTimeout(() => {
      this.loadingRoute.set(true);
      this.loadingShowTimer = null;
    }, 150);
  }

  private cancelLoadingShow(): void {
    if (this.loadingShowTimer) {
      clearTimeout(this.loadingShowTimer);
      this.loadingShowTimer = null;
    }
    this.loadingRoute.set(false);
  }

  openFeedback(): void {
    this.dialog.open(FeedbackDialogComponent, { width: '480px', maxWidth: '95vw' });
  }

  logout(): void {
    this.auth.logout();
  }

  /** Secret: rapidly click the title 5 times to toggle labs mode. */
  onTitleClick(): void {
    const now = Date.now();
    this.labsClicks.push(now);
    // Keep only clicks within the time window
    this.labsClicks = this.labsClicks.filter(t => now - t < LABS_CLICK_WINDOW);
    if (this.labsClicks.length >= LABS_CLICK_THRESHOLD) {
      this.labsClicks = [];
      this.features.setLabsMode(!this.features.labsMode());
    }
  }

  @HostListener('window:resize')
  checkScreenSize(): void {
    this.isMobile.set(window.innerWidth < MOBILE_BREAKPOINT);
  }
}
